import axios from 'axios';

// Browser-side default: same-origin '/api' (nginx proxies to the backend and
// it satisfies CSP connect-src 'self'). The old 'http://localhost:3001/api'
// fallback was unreachable from the browser and CSP-blocked, which silently
// broke client fetches (e.g. /docs CMS content). NEXT_PUBLIC_API_URL still wins
// when set at build time.
const BASE_URL = process.env.NEXT_PUBLIC_API_URL || '/api';

// HttpOnly cookies are the only auth transport on web — JWTs no longer
// touch JavaScript, so XSS can't steal them. The browser auto-sends
// `logixa_access` and `logixa_refresh` because of withCredentials:true and the
// API mounts cookieParser. Mobile clients still use Bearer headers via
// their own native HTTP layer.
const client = axios.create({ baseURL: BASE_URL, timeout: 15000, withCredentials: true });

// Auto-redirect on 401 (expired/revoked token).
client.interceptors.response.use(
  r => r,
  async err => {
    if (typeof window !== 'undefined') {
      const status = err.response?.status;
      if (status === 401) {
        // Clear any leftover localStorage from older builds during the
        // transition. Safe no-op for fresh installs.
        try {
          localStorage.removeItem('logixa_token');
          localStorage.removeItem('logixa-auth');
        } catch {/* private mode */}
        // Avoid redirect loops on the login page itself.
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
        // Fall through to throw a real error — do NOT `return` (that resolves the
        // request with `undefined`, so `.data` access throws "Cannot read
        // properties of undefined" instead of the backend's error message).
      }
    }
    const msg = err.response?.data?.message;
    throw new Error(Array.isArray(msg) ? msg.join(', ') : msg || err.message);
  },
);

export const api = {
  get: async <T = any>(url: string): Promise<T> => (await client.get(url)).data,
  post: async <T = any>(url: string, data?: any): Promise<T> => (await client.post(url, data)).data,
  patch: async <T = any>(url: string, data?: any): Promise<T> => (await client.patch(url, data)).data,
  delete: async <T = any>(url: string): Promise<T> => (await client.delete(url)).data,
};
